import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, ArrowRight } from "lucide-react";
import { Reveal, SectionHead } from "./shared";

const SERVICES = [
  {
    id: "remodels",
    num: "01",
    title: "Home Remodels",
    desc: "Kitchens, bathrooms, basements and whole-home refreshes. We tear out, rebuild and finish — one crew from demo to doorknobs.",
    specs: ["Kitchens", "Bathrooms", "Basements", "Flooring", "Drywall & Paint"],
    img: "https://images.unsplash.com/photo-1682888813913-e13f18692019?crop=entropy&cs=srgb&fm=jpg&q=85",
  },
  {
    id: "decks",
    num: "02",
    title: "Custom Decks",
    desc: "Pressure-treated, cedar and composite decks designed around how you actually live — grilling, lounging, or the whole neighborhood.",
    specs: ["Composite", "Cedar", "Pergolas", "Railings", "Skirting"],
    img: "https://images.pexels.com/photos/7587879/pexels-photo-7587879.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    id: "additions",
    num: "03",
    title: "Room Additions",
    desc: "Sunrooms, extra bedrooms, expanded living space. Foundation to roof, matched to your home's lines so it looks like it was always there.",
    specs: ["Foundations", "Framing", "Sunrooms", "Garage Conversions"],
    img: "https://images.unsplash.com/photo-1587582423116-ec07293f0395?crop=entropy&cs=srgb&fm=jpg&q=85",
  },
  {
    id: "fencing",
    num: "04",
    title: "Fencing",
    desc: "Privacy, picket, split-rail and gates. Set plumb and straight, with footings that survive frost and fence rows that survive the dog.",
    specs: ["Privacy", "Picket", "Gates", "Repairs", "Staining"],
    img: "https://images.unsplash.com/photo-1783870083960-18cd5c3a4e00?crop=entropy&cs=srgb&fm=jpg&q=85",
  },
  {
    id: "roofing",
    num: "05",
    title: "Roofing",
    desc: "Full replacements, repairs and storm damage. Tear-off, decking inspection, and a roof we'd stand behind in a hailstorm — literally.",
    specs: ["Asphalt Shingle", "Repairs", "Storm Damage", "Gutters", "Flashing"],
    img: "https://images.pexels.com/photos/33404248/pexels-photo-33404248.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    id: "tree",
    num: "06",
    title: "Tree Services",
    desc: "Removals, trimming and storm cleanup — rigged, cut and hauled safely away from your house, power lines and prize flowerbeds.",
    specs: ["Removal", "Trimming", "Stump Grinding", "Storm Cleanup"],
    img: "https://images.pexels.com/photos/31127615/pexels-photo-31127615.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    id: "handyman",
    num: "07",
    title: "Handyman Services",
    desc: "Honey-do lists, odd jobs and small repairs — sticky doors, dripping faucets, drywall patches, TV mounts. One visit, one invoice, done.",
    specs: ["Repairs", "Fixtures", "Drywall Patching", "Doors & Trim", "TV Mounting"],
    img: "https://images.pexels.com/photos/34938432/pexels-photo-34938432.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
];

const ServiceRow = ({ s, open, onToggle }) => (
  <div data-testid={`service-row-${s.id}`} className="border-t border-line last:border-b">
    <button
      onClick={onToggle}
      data-testid={`service-toggle-${s.id}`}
      className="w-full flex items-center gap-5 sm:gap-8 py-6 sm:py-8 px-2 sm:px-6 text-left group transition-colors duration-300 hover:bg-ink-card/50"
      aria-expanded={open}
    >
      <span className="font-mono text-xs text-safety font-bold w-8 shrink-0">{s.num}</span>
      <span className="font-display uppercase text-2xl sm:text-4xl lg:text-5xl tracking-tight text-bone group-hover:text-safety transition-colors duration-300 flex-1">
        {s.title}
      </span>
      <motion.span
        animate={{ rotate: open ? 45 : 0 }}
        transition={{ duration: 0.3 }}
        className={`w-10 h-10 shrink-0 border flex items-center justify-center ${open ? "border-safety bg-safety text-ink" : "border-line text-bone"}`}
      >
        <Plus className="w-5 h-5" strokeWidth={2.5} />
      </motion.span>
    </button>

    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="overflow-hidden"
          data-testid={`service-panel-${s.id}`}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 px-2 sm:px-6 pb-8 pt-2 items-center">
            <div>
              <p className="text-steel text-base leading-relaxed">{s.desc}</p>
              <div className="flex flex-wrap gap-2 mt-5">
                {s.specs.map((spec) => (
                  <span key={spec} className="font-mono text-[10px] uppercase tracking-[0.15em] text-steel border border-line px-3 py-1.5">
                    {spec}
                  </span>
                ))}
              </div>
              <a
                href="#quote"
                data-testid={`service-quote-link-${s.id}`}
                className="inline-flex items-center gap-2 mt-6 font-mono text-xs uppercase tracking-[0.2em] text-safety font-bold hover:gap-3 transition-all duration-200"
              >
                Get a {s.title.toLowerCase()} quote <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
              </a>
            </div>
            <div className="clip-notch-sm overflow-hidden h-56 md:h-64">
              <img src={s.img} alt={s.title} data-testid={`service-image-${s.id}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export default function Services() {
  const [openId, setOpenId] = useState("remodels");

  return (
    <section id="services" data-testid="services-section" className="py-20 md:py-32 bg-ink-card/40">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-8 lg:px-16">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
          <SectionHead
            eyebrow="What we do"
            title="Every trade. One crew."
            sub="Six services, zero subcontractor roulette. The same accountable team handles your project start to finish."
          />
        </div>
        <Reveal>
          <div>
            {SERVICES.map((s) => (
              <ServiceRow key={s.id} s={s} open={openId === s.id} onToggle={() => setOpenId(openId === s.id ? null : s.id)} />
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
